import {
  addMonths,
  daysBetween,
  weekday,
  type DateRange,
  type FundPeriod,
  type IncomeSchedule,
  type LocalDate,
} from '@tahi/core';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const monthIndex = (d: string) => Number(d.slice(5, 7)) - 1;
const shortMonth = (d: string) => (MONTHS[monthIndex(d)] ?? '').slice(0, 3);

export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

/** 'Fri, Nov 13' */
export function dayShort(d: LocalDate): string {
  return `${WEEKDAYS[weekday(d)]}, ${shortMonth(d)} ${Number(d.slice(8, 10))}`;
}

/** 'Dec 13' */
export function monthDay(d: LocalDate): string {
  return `${shortMonth(d)} ${Number(d.slice(8, 10))}`;
}

/** 'Apr 16, 2031' (a maturity date years away needs its year) */
export function dateWithYear(d: LocalDate): string {
  return `${shortMonth(d)} ${Number(d.slice(8, 10))}, ${d.slice(0, 4)}`;
}

/** 'November 13' (for screen readers) */
export function spokenDate(d: LocalDate): string {
  return `${MONTHS[monthIndex(d)]} ${Number(d.slice(8, 10))}`;
}

/** brand.md: "₱2,150 left until the 15th." `nextStart` is the day the money refills. */
export function untilText(nextStart: LocalDate): string {
  return `until the ${ordinal(Number(nextStart.slice(8, 10)))}`;
}

/** 'November 2026' */
export function monthLabel(month: string): string {
  return `${MONTHS[monthIndex(`${month}-01`)]} ${month.slice(0, 4)}`;
}

export function monthOf(d: LocalDate): string {
  return d.slice(0, 7);
}

export function shiftMonth(month: string, n: number): string {
  return addMonths(`${month}-01`, n).slice(0, 7);
}

/** 'Nov 13 – 29', or 'Nov 30 – Dec 14' across months */
export function rangeShort(r: DateRange): string {
  const start = `${shortMonth(r.start)} ${Number(r.start.slice(8, 10))}`;
  const endDay = Number(r.end.slice(8, 10));
  return r.start.slice(0, 7) === r.end.slice(0, 7)
    ? `${start} – ${endDay}`
    : `${start} – ${shortMonth(r.end)} ${endDay}`;
}

/** 'today', 'tomorrow', 'in 11 days' */
export function daysAwayText(today: LocalDate, d: LocalDate): string {
  const n = daysBetween(today, d);
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  return `in ${n} days`;
}

/** Reads what the user typed in a peso field: '18,000', '₱17,500.5', '250'. Null if it isn't an amount above zero. */
export function parsePesoInput(text: string): number | null {
  const t = text.replace(/[₱,\s]/g, '');
  const m = /^(\d{1,8})(?:\.(\d{1,2}))?$/.exec(t);
  if (!m) return null;
  const minor = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0') || '0');
  return minor > 0 ? minor : null;
}

/** 1800000 -> '18000', 1725050 -> '17250.50' (what an amount field starts with). */
export function minorToInput(minor: number): string {
  const pesos = Math.floor(minor / 100);
  const cents = minor % 100;
  return cents === 0 ? String(pesos) : `${pesos}.${String(cents).padStart(2, '0')}`;
}

export function dayOfMonthLabel(day: number): string {
  return day >= 31 ? 'last day' : ordinal(day);
}

/** 'Twice a month, 15th and last day', 'Every 2 weeks from Fri, Dec 4', 'Every Dec 15', ... */
export function scheduleSummary(s: IncomeSchedule): string {
  switch (s.kind) {
    case 'semi_monthly':
      return `Twice a month, ${dayOfMonthLabel(s.days[0])} and ${dayOfMonthLabel(s.days[1])}`;
    case 'monthly':
      return `Monthly, ${dayOfMonthLabel(s.day)}`;
    case 'biweekly':
      return `Every 2 weeks from ${dayShort(s.anchor)}`;
    case 'weekly':
      return `Weekly on ${WEEKDAYS[weekday(s.anchor)]}`;
    case 'yearly_dates':
      return `Every ${s.dates.map((md) => `${shortMonth(`2000-${md}`)} ${Number(md.slice(3, 5))}`).join(' and ')}`;
    case 'every_n_months':
      return s.n === 1
        ? `Every month from ${dayShort(s.anchor)}`
        : `Every ${s.n} months from ${dayShort(s.anchor)}`;
  }
}

export function periodLabel(p: FundPeriod): string {
  return p === 'weekly' ? 'Weekly' : p === 'monthly' ? 'Monthly' : 'Per cutoff';
}
